import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Feature, FeatureGuard } from "@sol/features";
import { Download, Pencil } from "@sol/icons";
import { Route } from "@sol/routing";
import {
    getResourceUuid,
    IClassroom,
    IClassroomUser,
    IUser,
    IUserClassroomsAsLearner,
    useClassroomBalanceSheetQuery,
} from "@sol/sdk";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";
import { Button } from "@sol/uikit/general/Button/Button";

import { SectionCard } from "@/shared/SectionCard";

import { LearnersExplorer } from "./components/LearnersExplorer";
import ApiErrorAlert from "../../components/ApiErrorAlert";

type Learner = IClassroomUser | IUser;

type Props = {
    id?: string;
    isPending?: boolean;
    learners: Learner[];
    classroom?: IClassroom;
};

export const ClassroomLearnersCard = ({ learners, classroom, isPending, ...rest }: Props) => {
    const [t] = useTranslation();

    const latestClassroom = useMemo(() => {
        if (classroom) {
            return classroom;
        }

        return learners.reduce((latest, learner) => {
            if (!("classroomsAsLearner" in learner) || !learner.classroomsAsLearner) {
                return latest;
            }

            return learner.classroomsAsLearner.reduce<IUserClassroomsAsLearner | null>(
                (latestClassroom, currentClassroom) => {
                    if (!latestClassroom) {
                        return currentClassroom;
                    }
                    return new Date(currentClassroom.startDate) > new Date(latestClassroom.startDate)
                        ? currentClassroom
                        : latestClassroom;
                },
                latest,
            );
        }, null);
    }, [classroom, learners]);

    const classroomId = latestClassroom ? (getResourceUuid(latestClassroom["@id"]) as string) : undefined;
    const downloadButtonRef = useRef(null);

    const [enableCsvRequest, setEnableCsvRequest] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const { data: csv, isFetching: isLoadingBalanceSheet } = useClassroomBalanceSheetQuery(
        { classroom: classroomId },
        {
            enabled: enableCsvRequest,
            onError: error => {
                setError(error);
            },
        },
    );

    const handleDownloadClick = () => {
        if (!classroomId || !latestClassroom) {
            return;
        }

        setEnableCsvRequest(true);
        setError(null);
    };

    // Handle the download when CSV data is available
    useEffect(() => {
        if (!csv || !enableCsvRequest || isLoadingBalanceSheet) {
            return;
        }

        const file = URL.createObjectURL(new Blob([csv]));

        try {
            const date = new Date();
            const filename = `${date.toLocaleDateString().split("/").join("")}-${latestClassroom?.title
                .toLowerCase()
                .split(" ")
                .join("-")}-balance-sheet.csv`;

            const linkElement = document.createElement("a");
            linkElement.href = file;
            linkElement.download = filename;
            linkElement.click();
        } finally {
            URL.revokeObjectURL(file);
            setEnableCsvRequest(false);
        }
    }, [csv, enableCsvRequest, isLoadingBalanceSheet, latestClassroom]);

    return (
        <SectionCard
            {...rest}
            data-cy="learners-card"
            title={
                isPending ? (
                    <Skeleton title={{ width: "60%" }} paragraph={false} active />
                ) : (
                    t("page.classrooms.home.learners.title")
                )
            }
            helper={
                !isPending &&
                latestClassroom && (
                    <>
                        <FeatureGuard feature={Feature.CLASSROOM_UPDATE_LEARNERS}>
                            <Link
                                href={{
                                    pathname: Route.CLASSROOMS_LEARNERS,
                                    query: {
                                        classroomId,
                                    },
                                }}
                                passHref
                            >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    data-cy="classroom-learners-update-button"
                                    title={t("component.classroomLearnersCard.editButton.title", {
                                        classroomTitle: latestClassroom.title,
                                    })}
                                >
                                    {t("component.classroomLearnersCard.editButton.text")} <Pencil />
                                </Button>
                            </Link>
                        </FeatureGuard>
                        <FeatureGuard feature={Feature.CLASSROOM_DOWNLOAD_BALANCE_SHEET}>
                            <Button
                                ref={downloadButtonRef}
                                variant="outlined"
                                color="primary"
                                data-cy="classroom-learners-download-balance-sheet"
                                onClick={handleDownloadClick}
                                loading={isLoadingBalanceSheet}
                                icon={<Download />}
                                iconPosition="end"
                            >
                                {t("component.classroomLearnersCard.downloadButton.text")}
                            </Button>
                        </FeatureGuard>
                    </>
                )
            }
        >
            <LearnersExplorer learners={learners} />
            <ApiErrorAlert error={error} dissmiss={() => setError(null)} triggerElement={downloadButtonRef} />
        </SectionCard>
    );
};
